
// Define MediaRecorderOptions if not available in global scope
interface MediaRecorderOptions {
  mimeType?: string;
  audioBitsPerSecond?: number;
  videoBitsPerSecond?: number;
  bitsPerSecond?: number;
}

export class AudioRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private stream: MediaStream | null = null;
  private onDataAvailable: ((data: Blob) => void) | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private destination: MediaStreamAudioDestinationNode | null = null;
  private vadThreshold = -50; // Decibels
  private isSilent = true;

  constructor(onDataAvailable?: (data: Blob) => void) {
    this.onDataAvailable = onDataAvailable || null;
  }

  async start(): Promise<void> {
    if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
      throw new Error('Media devices not supported');
    }

    try {
      // 1. Capture stream with standard hardware-friendly constraints
      this.stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        },
        video: false
      });
      
      // 2. Initialize AudioContext at exactly 16,000 Hz for downsampling
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: 16000
      });
      
      // 3. Connect original stream source to the 16kHz destination node
      const source = this.audioContext.createMediaStreamSource(this.stream);
      this.destination = this.audioContext.createMediaStreamDestination();
      source.connect(this.destination);

      // 4. Connect analyzer to original source for visualizer / VAD checks
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;
      source.connect(this.analyser);

      console.log('AudioRecorder: Microphone stream resampled to 16kHz and VAD initialized.');
    } catch (error: any) {
      console.error('Error accessing microphone:', error);
      
      if (window.parent !== window) {
        window.parent.postMessage({ type: 'MODULAR_MIC_ERROR' }, '*');
      }

      let friendlyMessage = 'Error al acceder al micrófono.';
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError' || error.message?.includes('dismissed')) {
        friendlyMessage = 'Permiso de micrófono denegado. Por favor, asegúrate de permitir el acceso al micrófono.';
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        friendlyMessage = 'No se detectó ningún micrófono conectado.';
      }
      
      throw new Error(friendlyMessage);
    }

    try {
      // Formato comprimido Opus @ 32kbps
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus' 
        : (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus') ? 'audio/ogg;codecs=opus' : 'audio/mp4');
        
      const options: MediaRecorderOptions = {
        mimeType,
        audioBitsPerSecond: 32000 // Colchón de seguridad acústica de 32 kbps para susurros
      };

      // Gravar desde el stream remuestreado a 16kHz
      this.mediaRecorder = new MediaRecorder(this.destination.stream, options);
      this.audioChunks = [];

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
          if (this.onDataAvailable) {
            this.onDataAvailable(event.data);
          }
        }
      };

      // Solicitar chunks cada 200ms
      this.mediaRecorder.start(200); 
    } catch (error) {
      console.error('Error starting MediaRecorder:', error);
      throw error;
    }
  }

  private checkVoiceActivity(): boolean {
    if (!this.analyser) return true;
    
    const dataArray = new Float32Array(this.analyser.fftSize);
    this.analyser.getFloatTimeDomainData(dataArray);
    
    // Calculate RMS (Root Mean Square)
    let sumSquares = 0;
    for (let i = 0; i < dataArray.length; i++) {
        sumSquares += dataArray[i] * dataArray[i];
    }
    const rms = Math.sqrt(sumSquares / dataArray.length);
    
    // Convert to Decibels
    const db = 20 * Math.log10(rms || 0.00001); // Avoid log(0)
    
    return db > this.vadThreshold;
  }

  getStream(): MediaStream | null {
    return this.stream;
  }

  stop(): Promise<{ blob: Blob; mimeType: string }> {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder) {
        reject('Recorder not initialized');
        return;
      }

      this.mediaRecorder.onstop = async () => {
        const mimeType = this.mediaRecorder?.mimeType || 'audio/webm';
        
        // Final sanity check: if no chunks were collected (all silent), we return an empty blob
        const audioBlob = this.audioChunks.length > 0 
          ? new Blob(this.audioChunks, { type: mimeType })
          : new Blob([], { type: mimeType });
        
        this.stream?.getTracks().forEach(track => track.stop());
        
        if (this.audioContext && this.audioContext.state !== 'closed') {
          try {
            await this.audioContext.close();
          } catch (e) {
            console.error('Error closing AudioContext:', e);
          }
        }
        
        this.mediaRecorder = null;
        this.stream = null;
        this.audioContext = null;
        this.analyser = null;
        this.destination = null;
        
        resolve({ blob: audioBlob, mimeType });
      };

      if (this.mediaRecorder.state === 'recording') {
         this.mediaRecorder.requestData();
      }
      
      this.mediaRecorder.stop();
    });
  }
}

export const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      const base64Data = base64String.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Optimiza el audio para el motor de transcripción.
 * ELIMINADA NORMALIZACIÓN WAV POR REQUISITO MÉDICO. Siempre mantenemos el formato original comprimido.
 */
export const compressAudio = async (blob: Blob): Promise<{ blob: Blob; mimeType: string }> => {
  // Ahora simplemente retornamos el blob original, ya que el grabador 
  // ya sale comprimido a 32kbps Opus.
  return { blob, mimeType: blob.type };
};

export const getAudioDuration = async (blob: Blob): Promise<number> => {
  if (blob.size === 0) return 0;
  return new Promise((resolve) => {
    const audio = new Audio();
    audio.src = URL.createObjectURL(blob);
    audio.onloadedmetadata = () => {
      URL.revokeObjectURL(audio.src);
      resolve(audio.duration);
    };
    audio.onerror = () => resolve(0);
  });
};

/**
 * Inicializa la captura de audio forzando un remuestreo nativo a 16 kHz
 * mediante Web Audio API para optimizar el pipeline de Gemini.
 */
export async function iniciarCapturaMedica16kHz(): Promise<{
  mediaRecorder: MediaRecorder;
  audioContext: AudioContext;
  streamOriginal: MediaStream;
}> {
  // 1. Capturar el stream en la resolución nativa que exija el hardware (Evita fallos de inicio)
  const streamOriginal = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      noiseSuppression: true
    },
    video: false
  });

  // 2. Instanciar el AudioContext forzando explícitamente los 16,000 Hz nativos
  const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
    sampleRate: 16000
  });

  // 3. Crear el nodo puente de origen (inyecta los 48kHz/44.1kHz del hardware)
  const fuenteOrigen = audioContext.createMediaStreamSource(streamOriginal);

  // 4. Crear el nodo de destino (recibe el audio ya diezmado/remuestreado a 16kHz por el contexto)
  const destinoRemuestreado = audioContext.createMediaStreamDestination();

  // 5. Conectar los nodos en el hilo de audio del navegador
  fuenteOrigen.connect(destinoRemuestreado);

  // 6. Configurar el MediaRecorder utilizando EXCLUSIVAMENTE el stream de 16kHz pre-calculado
  const opcionesRecorder: MediaRecorderOptions = {
    mimeType: 'audio/webm;codecs=opus',
    audioBitsPerSecond: 32000 // Colchón de seguridad acústica de 32 kbps para susurros
  };

  const mediaRecorder = new MediaRecorder(destinoRemuestreado.stream, opcionesRecorder);

  return {
    mediaRecorder,
    audioContext,
    streamOriginal
  };
}

/**
 * Detiene de forma segura el hardware y libera los contextos de audio
 */
export async function detenerCapturaMedica(
  mediaRecorder: MediaRecorder,
  audioContext: AudioContext,
  streamOriginal: MediaStream
): Promise<void> {
  if (mediaRecorder.state !== 'inactive') {
    mediaRecorder.stop();
  }

  // Apagar hardware físico
  streamOriginal.getTracks().forEach(track => track.stop());

  // Cerrar el hilo de Web Audio API para evitar fugas de memoria
  if (audioContext.state !== 'closed') {
    await audioContext.close();
  }
}

